import { 
  auth, 
  onAuthStateChanged,
  signOut as firebaseSignOut
} from './firebaseConfig';
import { User } from 'firebase/auth';

/**
 * Persistent Authentication Service
 * Handles auth state persistence, token refresh, and session management
 */

interface AuthSession {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  lastSignIn: number;
}

class AuthService {
  private sessionKey = 'famitry_auth_session';
  private tokenRefreshInterval: NodeJS.Timeout | null = null;

  /**
   * Initialize auth listener and restore session from storage
   */
  initializeAuth(onAuthChange: (user: User | null) => void): () => void {
    // Setup Firebase listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Store session
        this.saveSession(user);
        // Refresh token periodically (Firebase handles this, but we can add extra safety)
        this.startTokenRefresh(user);
      } else {
        // Clear session
        this.clearSession();
        this.stopTokenRefresh();
      }
      onAuthChange(user);
    });

    // Restore previous session if available
    this.restoreSession();

    return unsubscribe;
  }

  /**
   * Save auth session to localStorage
   */
  private saveSession(user: User): void {
    const session: AuthSession = {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || undefined,
      photoURL: user.photoURL || undefined,
      lastSignIn: Date.now()
    };
    localStorage.setItem(this.sessionKey, JSON.stringify(session));
  }

  /**
   * Restore session from localStorage
   */
  private restoreSession(): void {
    const sessionStr = localStorage.getItem(this.sessionKey);
    if (sessionStr) {
      try {
        const session: AuthSession = JSON.parse(sessionStr);
        const sessionAge = Date.now() - session.lastSignIn;
        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        
        // Only restore if session is less than 1 week old
        if (sessionAge < oneWeek) {
          console.log('Restored session for user:', session.email);
        } else {
          this.clearSession();
        }
      } catch (e) {
        console.error('Failed to restore session:', e);
        this.clearSession();
      }
    }
  }

  /**
   * Clear session from localStorage
   */
  private clearSession(): void {
    localStorage.removeItem(this.sessionKey);
    localStorage.removeItem('famitry_firebase_uid');
    localStorage.removeItem('famitry_email');
  }

  /**
   * Start periodic token refresh to keep auth alive
   */
  private startTokenRefresh(user: User): void {
    // Clear existing interval
    this.stopTokenRefresh();

    // Firebase automatically handles token refresh, but we can add a safety check
    // Refresh token every 50 minutes (Firebase default is 60 minutes)
    this.tokenRefreshInterval = setInterval(async () => {
      try {
        await user.getIdToken(true); // Force refresh
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, 50 * 60 * 1000);
  }

  /**
   * Stop token refresh interval
   */
  private stopTokenRefresh(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }

  /**
   * Get current session data
   */
  getSession(): AuthSession | null {
    const sessionStr = localStorage.getItem(this.sessionKey);
    if (sessionStr) {
      try {
        return JSON.parse(sessionStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Sign out and clear session
   */
  async signOut(): Promise<void> {
    this.stopTokenRefresh();
    this.clearSession();
    await firebaseSignOut(auth);
  }

  /**
   * Get current auth token
   */
  async getToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Re-export for convenience
export { onAuthStateChanged, firebaseSignOut };
